import { Message, MessageFailureKind } from '../models/message';
import { formatMessageTime } from '../mappers/mappers';

/**
 * Pure message-list reconciliation for the Concierge chat.
 *
 * Turns: every message of a turn carries the client's `clientTurnId` (the
 * idempotency key) and the server's `turnId` (a different value). Messages are
 * grouped by `clientTurnId`, falling back to `turnId` only when it is absent.
 *
 * Invariants:
 * - Messages are keyed by `id`; a server copy of a message already on screen
 *   updates it in place (never appends a duplicate).
 * - Local stand-ins (`isOptimistic`) are replaced by the server copy of the
 *   same sender + turn, or, as a last resort when the server omits the turn
 *   ids, the same sender + text.
 * - Nothing on screen is dropped just because a history snapshot does not
 *   contain it (it may simply not be persisted yet).
 * - Superseded ProviderError bubbles are hidden at render time
 *   (`selectVisibleMessages`), never removed from state.
 */

// ---------------------------------------------------------------------------
// turn identity
// ---------------------------------------------------------------------------

/** The key a message's turn is tracked under: `clientTurnId`, else the server `turnId`. */
export function turnKeyOf(message: Message): string | undefined {
   return message.clientTurnId ?? message.turnId;
}

/** Two messages belong to the same turn (clientTurnId first, server turnId only as a fallback). */
export function isSameTurn(a: Message, b: Message): boolean {
   if (a.clientTurnId && b.clientTurnId) return a.clientTurnId === b.clientTurnId;
   if (a.turnId && b.turnId) return a.turnId === b.turnId;
   return false;
}

/** Two messages carry turn ids that prove they are different turns. */
function isDifferentTurn(a: Message, b: Message): boolean {
   if (a.clientTurnId && b.clientTurnId && a.clientTurnId !== b.clientTurnId) return true;
   return !!(a.turnId && b.turnId && a.turnId !== b.turnId);
}

/**
 * Resolves each message to its turn key. A message that only has a server
 * `turnId` joins the `clientTurnId` of any sibling that carries both.
 */
function turnKeyResolver(messages: Message[]): (m: Message) => string | undefined {
   const clientIdByServerTurn = new Map<string, string>();
   messages.forEach((m) => {
      if (m.turnId && m.clientTurnId && !clientIdByServerTurn.has(m.turnId)) clientIdByServerTurn.set(m.turnId, m.clientTurnId);
   });
   return (m) => m.clientTurnId ?? (m.turnId ? clientIdByServerTurn.get(m.turnId) ?? m.turnId : undefined);
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function textOf(message: Message): string {
   return typeof message.text === 'string' ? message.text.trim() : '';
}

/** Merge a server copy over the message it replaces, keeping client-only identity. */
export function mergeMessage(current: Message, incoming: Message): Message {
   const merged: Message = { ...incoming };
   const clientTurnId = incoming.clientTurnId ?? current.clientTurnId;
   if (clientTurnId !== undefined) merged.clientTurnId = clientTurnId;
   const turnId = incoming.turnId ?? current.turnId;
   if (turnId !== undefined) merged.turnId = turnId;
   const language = incoming.language ?? current.language;
   if (language !== undefined) merged.language = language;
   // Only carry a key that differs from the new id, so repeated merges are idempotent.
   const renderKey = current.renderKey ?? current.id;
   if (renderKey && renderKey !== merged.id) merged.renderKey = renderKey;
   return merged;
}

/** Keeps the first occurrence of each id; id-less messages are always kept. */
export function dedupeById(messages: Message[]): Message[] {
   const seen = new Set<string>();
   return messages.filter((m) => {
      if (!m.id) return true;
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
   });
}

function canTextMatch(local: Message, server: Message): boolean {
   const text = textOf(local);
   return text !== '' && local.sender === server.sender && !isDifferentTurn(local, server) && textOf(server) === text;
}

/**
 * A server message for a turn proves the server has its guest message: a local
 * guest message of that turn that is still pending or marked failed is settled
 * as sent (so "Not sent / Retry" never sits next to its own answer).
 * Conflicts are left alone — they are about different text under the same id.
 */
export function settleAnsweredTurns(messages: Message[]): Message[] {
   const keyOf = turnKeyResolver(messages);
   const answered = new Set<string>();
   messages.forEach((m) => {
      const key = keyOf(m);
      if (m.sender === 'bot' && key && !m.isOptimistic) answered.add(key);
   });
   if (answered.size === 0) return messages;

   let changed = false;
   const next = messages.map((m) => {
      const key = keyOf(m);
      if (m.sender !== 'user' || !m.isOptimistic || !key || !answered.has(key)) return m;
      if (m.deliveryStatus === 'sent' || m.failureKind === 'conflict') return m;
      changed = true;
      return { ...m, deliveryStatus: 'sent' as const, failureKind: undefined };
   });
   return changed ? next : messages;
}

// ---------------------------------------------------------------------------
// single message (SignalR)
// ---------------------------------------------------------------------------

/**
 * Inserts or updates one server message (e.g. from SignalR `ReceiveMessage`).
 */
export function upsertMessage(current: Message[], incoming: Message): Message[] {
   let index = incoming.id ? current.findIndex((m) => m.id === incoming.id) : -1;

   if (index === -1 && turnKeyOf(incoming)) {
      index = current.findIndex((m) => m.isOptimistic && m.sender === incoming.sender && isSameTurn(m, incoming));
   }

   if (index === -1 && !incoming.isOptimistic) {
      index = current.findIndex((m) => m.isOptimistic && canTextMatch(m, incoming));
   }

   if (index === -1) {
      return settleAnsweredTurns([...current, incoming]);
   }

   const next = current.slice();
   next[index] = mergeMessage(current[index], incoming);
   // Defensive: never leave two entries with the same id behind.
   return settleAnsweredTurns(dedupeById(next));
}

// ---------------------------------------------------------------------------
// full history (GET /api/chat)
// ---------------------------------------------------------------------------

/**
 * Reconciles what is on screen with a history snapshot from the server.
 *
 * Server messages appear in server order. Messages that are only on screen
 * (pending/failed sends, the ProviderError placeholder, the local welcome
 * message, or a SignalR message the snapshot does not contain yet) are kept
 * right after the message they followed before the refetch.
 */
export function reconcileMessages(current: Message[], serverMessages: Message[]): Message[] {
   const incoming = dedupeById(serverMessages);
   const incomingIds = new Set(incoming.map((m) => m.id).filter((id): id is string => !!id));
   const currentIds = new Set(current.map((m) => m.id).filter((id): id is string => !!id));

   /** current index -> id of the incoming message that replaces it */
   const replacedBy = new Map<number, string>();
   /** incoming id -> current index it replaces */
   const pairedWith = new Map<string, number>();
   /** current indexes superseded by an incoming message already paired elsewhere */
   const dropped = new Set<number>();

   const pair = (currentIndex: number, incomingId: string) => {
      replacedBy.set(currentIndex, incomingId);
      pairedWith.set(incomingId, currentIndex);
   };

   // 1. Same id.
   current.forEach((m, i) => {
      if (m.id && incomingIds.has(m.id) && !pairedWith.has(m.id)) pair(i, m.id);
   });

   // 2. Optimistic stand-in of the same turn.
   current.forEach((m, i) => {
      if (replacedBy.has(i) || !m.isOptimistic || !turnKeyOf(m)) return;

      if (m.sender === 'user') {
         // A turn has exactly one guest message: its server copy replaces the
         // stand-in, or makes it redundant if that copy is already on screen.
         const match = incoming.find((s) => s.id && s.sender === 'user' && isSameTurn(m, s));
         if (!match?.id) return;
         if (pairedWith.has(match.id)) {
            dropped.add(i);
         } else {
            pair(i, match.id);
         }
         return;
      }

      // An assistant stand-in (ProviderError placeholder) is only replaced by an
      // assistant message of the turn that is new to this client — never by an
      // older reply that is already on screen (a turn can have several).
      const match = incoming.find((s) => s.id && s.sender === m.sender && isSameTurn(m, s) && !currentIds.has(s.id) && !pairedWith.has(s.id));
      if (match?.id) pair(i, match.id);
   });

   // 3. Optimistic stand-in with the same sender + text, only against server
   //    messages that are new to this client (never re-claim an old message).
   current.forEach((m, i) => {
      if (replacedBy.has(i) || dropped.has(i) || !m.isOptimistic) return;
      const match = incoming.find((s) => s.id && !pairedWith.has(s.id) && !currentIds.has(s.id) && canTextMatch(m, s));
      if (match?.id) pair(i, match.id);
   });

   // Group the survivors by the server message they followed.
   const survivorsAfter = new Map<string | null, Message[]>();
   let anchor: string | null = null;
   current.forEach((m, i) => {
      if (dropped.has(i)) return;
      const replacement = replacedBy.get(i);
      if (replacement) {
         anchor = replacement;
         return;
      }
      const bucket = survivorsAfter.get(anchor) ?? [];
      bucket.push(m);
      survivorsAfter.set(anchor, bucket);
   });

   const result: Message[] = [...(survivorsAfter.get(null) ?? [])];
   incoming.forEach((s) => {
      const currentIndex = s.id ? pairedWith.get(s.id) : undefined;
      result.push(currentIndex !== undefined ? mergeMessage(current[currentIndex], s) : s);
      if (s.id) result.push(...(survivorsAfter.get(s.id) ?? []));
   });

   return settleAnsweredTurns(dedupeById(result));
}

// ---------------------------------------------------------------------------
// what to render
// ---------------------------------------------------------------------------

/**
 * Supersede rule: within a turn, an assistant `ProviderError` message is hidden
 * once a newer assistant message of the same turn exists (a successful re-run,
 * or a later error). Non-error assistant messages are never hidden. The input
 * is not modified, so reconciliation over the full state stays idempotent.
 * Returns the same array when nothing is hidden.
 */
export function selectVisibleMessages(messages: Message[]): Message[] {
   const keyOf = turnKeyResolver(messages);
   const lastAssistantIndex = new Map<string, number>();
   messages.forEach((m, i) => {
      const key = m.sender === 'bot' ? keyOf(m) : undefined;
      if (key) lastAssistantIndex.set(key, i);
   });

   let hiddenAny = false;
   const visible = messages.filter((m, i) => {
      if (m.sender !== 'bot' || m.outcome !== 'ProviderError') return true;
      const key = keyOf(m);
      if (!key || lastAssistantIndex.get(key) === i) return true;
      hiddenAny = true;
      return false;
   });
   return hiddenAny ? visible : messages;
}

// ---------------------------------------------------------------------------
// send lifecycle (all keyed by clientTurnId)
// ---------------------------------------------------------------------------

export function createOptimisticUserMessage(params: { text: string; clientTurnId: string; language?: string; now?: Date }): Message {
   const now = params.now ?? new Date();
   return {
      id: `local-user-${params.clientTurnId}`,
      text: params.text,
      sender: 'user',
      timestamp: formatMessageTime(now),
      createdAt: now.toISOString(),
      clientTurnId: params.clientTurnId,
      language: params.language,
      isOptimistic: true,
      deliveryStatus: 'pending'
   };
}

export function findUserMessageForTurn(messages: Message[], turnKey: string): Message | undefined {
   const keyOf = turnKeyResolver(messages);
   return messages.find((m) => m.sender === 'user' && keyOf(m) === turnKey);
}

/** Sets the delivery status of the local (optimistic) guest message of a turn. */
export function markTurnStatus(messages: Message[], clientTurnId: string, status: 'pending' | 'sent'): Message[] {
   let changed = false;
   const next = messages.map((m) => {
      if (m.sender !== 'user' || turnKeyOf(m) !== clientTurnId || !m.isOptimistic) return m;
      if (m.deliveryStatus === status && !m.failureKind) return m;
      changed = true;
      return { ...m, deliveryStatus: status, failureKind: undefined };
   });
   return changed ? next : messages;
}

/** Marks the turn's local guest message as failed and removes its local placeholders. */
export function markTurnFailed(messages: Message[], clientTurnId: string, kind: MessageFailureKind): Message[] {
   return removeTurnPlaceholders(messages, clientTurnId).map((m) =>
      m.sender === 'user' && turnKeyOf(m) === clientTurnId && m.isOptimistic ? { ...m, deliveryStatus: 'failed', failureKind: kind } : m
   );
}

export function providerErrorPlaceholderId(clientTurnId: string): string {
   return `local-provider-error-${clientTurnId}`;
}

/** Local assistant bubbles that stand in for a turn's answer (the ProviderError placeholder). */
function isTurnPlaceholder(m: Message, clientTurnId: string): boolean {
   return m.sender === 'bot' && !!m.isOptimistic && turnKeyOf(m) === clientTurnId;
}

export function removeTurnPlaceholders(messages: Message[], clientTurnId: string): Message[] {
   const next = messages.filter((m) => !isTurnPlaceholder(m, clientTurnId));
   return next.length === messages.length ? messages : next;
}

/**
 * Inserts the local ProviderError bubble for a turn right after the turn's
 * last message, so it is the newest assistant message of the turn (and
 * supersedes an older server ProviderError). Idempotent.
 */
export function addProviderErrorPlaceholder(messages: Message[], clientTurnId: string, now: Date = new Date()): Message[] {
   const id = providerErrorPlaceholderId(clientTurnId);
   if (messages.some((m) => m.id === id)) return messages;

   const keyOf = turnKeyResolver(messages);
   const userMessage = messages.find((m) => m.sender === 'user' && keyOf(m) === clientTurnId);
   const placeholder: Message = {
      id,
      text: '',
      sender: 'bot',
      timestamp: formatMessageTime(now),
      createdAt: now.toISOString(),
      clientTurnId,
      outcome: 'ProviderError',
      isOptimistic: true
   };
   if (userMessage?.turnId) placeholder.turnId = userMessage.turnId;

   let lastIndex = -1;
   messages.forEach((m, i) => {
      if (keyOf(m) === clientTurnId) lastIndex = i;
   });
   if (lastIndex === -1) return [...messages, placeholder];

   const next = messages.slice();
   next.splice(lastIndex + 1, 0, placeholder);
   return next;
}

/** Retry prep: drop local placeholders and put the local guest message back to pending. */
export function prepareTurnRetry(messages: Message[], clientTurnId: string): Message[] {
   return markTurnStatus(removeTurnPlaceholders(messages, clientTurnId), clientTurnId, 'pending');
}

// ---------------------------------------------------------------------------
// turn resolution
// ---------------------------------------------------------------------------

/** Fingerprints of the assistant messages that already exist for a turn (id -> signature). */
export type TurnBaseline = Record<string, string>;

function signature(m: Message): string {
   return `${m.outcome ?? ''}|${textOf(m)}`;
}

/**
 * Assistant messages that count as "the answer" for a turn.
 *
 * Primary rule: an assistant message of the same turn (clientTurnId, else turnId).
 * Fallback for servers that do not echo turn ids: an assistant message after
 * the turn's guest message whose turn is absent or unknown locally.
 */
function answersForTurn(messages: Message[], turnKey: string): Message[] {
   const keyOf = turnKeyResolver(messages);
   const userIndex = messages.findIndex((m) => m.sender === 'user' && keyOf(m) === turnKey);
   const knownTurns = new Set<string>();
   messages.forEach((m) => {
      const key = m.sender === 'user' ? keyOf(m) : undefined;
      if (key) knownTurns.add(key);
   });
   return messages.filter((m, i) => {
      if (m.sender !== 'bot') return false;
      const key = keyOf(m);
      if (key === turnKey) return true;
      return userIndex !== -1 && i > userIndex && (!key || !knownTurns.has(key));
   });
}

/**
 * Captures the ProviderError messages already present for a turn, so retrying
 * a turn that failed on the server waits for a NEW (or updated) assistant
 * message. A real answer that is already on screen is never baselined: it
 * still resolves the turn (the server replays completed turns).
 */
export function captureTurnBaseline(messages: Message[], turnKey: string): TurnBaseline {
   const baseline: TurnBaseline = {};
   answersForTurn(messages, turnKey).forEach((m) => {
      if (m.id && m.outcome === 'ProviderError') baseline[m.id] = signature(m);
   });
   return baseline;
}

/**
 * True once an assistant message for the turn is on screen (a real answer, a
 * server outcome, or the local ProviderError placeholder) that was not already
 * there, unchanged, when `baseline` was captured.
 */
export function isTurnAnswered(messages: Message[], turnKey: string, baseline: TurnBaseline = {}): boolean {
   return answersForTurn(messages, turnKey).some((m) => !m.id || baseline[m.id] !== signature(m));
}

// ---------------------------------------------------------------------------
// retry target
// ---------------------------------------------------------------------------

export interface RetryTarget {
   /** Always the turn's ORIGINAL clientTurnId — a Retry never mints a new id. */
   clientTurnId: string;
   /** The ORIGINAL guest text. */
   text: string;
   language?: string;
}

/**
 * Works out what a Retry on `messageId` re-sends: the original guest text with
 * the original `clientTurnId`. Works for a failed guest message and for a
 * ProviderError assistant message (local placeholder or server-sent).
 * Returns undefined (no Retry offered) when the turn's guest message or its
 * `clientTurnId` is unknown.
 */
export function resolveRetryTarget(messages: Message[], messageId: string): RetryTarget | undefined {
   const message = messages.find((m) => m.id === messageId);
   if (!message) return undefined;

   let userMessage: Message | undefined;
   if (message.sender === 'user') {
      userMessage = message;
   } else {
      const key = turnKeyResolver(messages)(message);
      if (key) userMessage = findUserMessageForTurn(messages, key);
   }

   const clientTurnId = userMessage?.clientTurnId ?? message.clientTurnId;
   if (!userMessage || !clientTurnId || typeof userMessage.text !== 'string' || userMessage.text.trim() === '') return undefined;
   return { clientTurnId, text: userMessage.text, language: userMessage.language };
}
